import { ed25519 } from '@noble/curves/ed25519';
import { secp256k1 } from '@noble/curves/secp256k1';
import { hkdf } from '@noble/hashes/hkdf';
import { sha256 } from '@noble/hashes/sha256';
import { EVM_HKDF_INFO } from './constants';

export interface DerivedKeys {
  /** Ed25519 32-byte secret key seed; used for Cere + Solana signing. */
  edSeed: Uint8Array;
  /** secp256k1 32-byte secret key; used for EVM signing. */
  secpKey: Uint8Array;
  /** 32-byte Ed25519 public key derived from edSeed. */
  edPubkey: Uint8Array;
  /** 65-byte uncompressed secp256k1 public key (0x04 || X || Y) derived from secpKey. */
  secpPubkeyUncompressed: Uint8Array;
}

/**
 * Derive the v2 session keys from a WebAuthn PRF output. Spec §3.2.
 *
 *   edSeed  = prfOutput
 *   secpKey = HKDF-SHA256(prfOutput, salt="", info="cere-wallet-evm-secp256k1-v1", L=32)
 *
 * @param prfOutput 32-byte PRF output from `prf.results.first`.
 */
export function derivedKeys(prfOutput: Uint8Array): DerivedKeys {
  if (prfOutput.length !== 32) {
    throw new Error(`expected 32-byte PRF output, got ${prfOutput.length}`);
  }
  const edSeed = new Uint8Array(prfOutput); // copy to detach from caller's buffer
  const secpKey = hkdf(sha256, prfOutput, new Uint8Array(0), EVM_HKDF_INFO, 32);

  const edPubkey = ed25519.getPublicKey(edSeed);
  // @noble/curves exposes both compressed and uncompressed forms; we want uncompressed.
  const secpPubkeyUncompressed = secp256k1.getPublicKey(secpKey, false);

  return { edSeed, secpKey, edPubkey, secpPubkeyUncompressed };
}
